/**
 * What MyRave sells, and what each thing gives — ONE catalogue for the API,
 * the web app and the phone app (the user, 2026-10-07, evening: "MyRave sets
 * the prices; a festival only chooses whether its rave is paid or free").
 *
 * Prices are US-dollar cents (hard rule: money is integer minor units). The
 * web charges them through Stripe Checkout; the stores charge their own price
 * points for the same products, configured in App Store Connect and Google
 * Play Console under `store` below — the store's price is what the store
 * charges, and what a store purchase GIVES is decided here, never by the
 * store's answer.
 *
 * One product id on both stores (letters, digits and underscores — Google's
 * rule is the stricter one), so there is one list to type into two consoles.
 * Tiers are auto-renewing yearly subscriptions: on Apple one subscription
 * group ("MyRave plans") holding all three; on Google one subscription per
 * tier with a base plan `yearly`.
 */

import { RAVE_PRICE_PRODUCTS, type RavePriceProductId } from './ravePlans.js';

export type ProductId =
  | 'RAVE_SINGLE'
  | 'RAVE_PACK_3'
  | 'PHOTO'
  /** The photo at PLUS's 40% off — a product of its own, because a store price cannot depend on the buyer. */
  | 'PHOTO_PLUS'
  | 'TIER_LITE'
  | 'TIER_PLUS'
  | 'TIER_MAX';

export type Tier = 'LITE' | 'PLUS' | 'MAX';

/** A catalogue product, or one Independent Rave price step (`ravePlans.ts`). */
export type AnyProductId = ProductId | RavePriceProductId | DonationProductId;

export interface Product {
  id: AnyProductId;
  /** US-dollar cents. */
  priceCents: number;
  kind: 'one-time' | 'yearly';
  /** What it gives, for the API to grant and the screens to say. */
  gives:
    | { rave: 'one' }
    | { raveCredits: number }
    | { photo: 'one' }
    | { tier: Tier }
    /** A donation (step 108): nothing to grant — a thank-you and a payment row. */
    | { donation: true };
  /** The id in App Store Connect AND Google Play Console. */
  store: { productId: string; basePlan?: string };
}

/** Raves a tier may open per year of its subscription; null = every rave. */
export const TIER_RAVES_PER_YEAR: Readonly<Record<Tier, number | null>> = { LITE: 5, PLUS: 10, MAX: null };
/** Free photo originals per year; null = every photo. */
export const TIER_FREE_PHOTOS_PER_YEAR: Readonly<Record<Tier, number | null>> = { LITE: 0, PLUS: 10, MAX: null };
/** PLUS: 40% off a photo once its ten free ones are used. */
export const PLUS_PHOTO_DISCOUNT_PERCENT = 40;

export const PRODUCTS: Readonly<Record<ProductId, Product>> = {
  RAVE_SINGLE: { id: 'RAVE_SINGLE', priceCents: 500, kind: 'one-time', gives: { rave: 'one' }, store: { productId: 'rave_single' } },
  // "2 + 1": three credits for the price of two, and they never expire.
  RAVE_PACK_3: { id: 'RAVE_PACK_3', priceCents: 1000, kind: 'one-time', gives: { raveCredits: 3 }, store: { productId: 'rave_pack3' } },
  PHOTO: { id: 'PHOTO', priceCents: 800, kind: 'one-time', gives: { photo: 'one' }, store: { productId: 'photo_single' } },
  PHOTO_PLUS: {
    id: 'PHOTO_PLUS', priceCents: 800 - (800 * PLUS_PHOTO_DISCOUNT_PERCENT) / 100, kind: 'one-time',
    gives: { photo: 'one' }, store: { productId: 'photo_plus' },
  },
  TIER_LITE: { id: 'TIER_LITE', priceCents: 1900, kind: 'yearly', gives: { tier: 'LITE' }, store: { productId: 'tier_lite', basePlan: 'yearly' } },
  TIER_PLUS: { id: 'TIER_PLUS', priceCents: 3900, kind: 'yearly', gives: { tier: 'PLUS' }, store: { productId: 'tier_plus', basePlan: 'yearly' } },
  TIER_MAX: { id: 'TIER_MAX', priceCents: 7900, kind: 'yearly', gives: { tier: 'MAX' }, store: { productId: 'tier_max', basePlan: 'yearly' } },
};

export const PRODUCT_IDS = Object.keys(PRODUCTS) as ProductId[];

export const isProductId = (v: unknown): v is ProductId => typeof v === 'string' && Object.prototype.hasOwnProperty.call(PRODUCTS, v);

/**
 * A donation to MyRave after a rave (step 108, the user, 2026-11-02: "donate a few bucks, 1, 3, 5, 10 $"): four
 * CONSUMABLE store products (the phone app) that the web sells through Stripe (Apple Pay, Google Pay, PayPal). Fixed
 * prices, outside the console's catalogue; they grant nothing.
 */
export const DONATION_AMOUNTS = [1, 3, 5, 10] as const;
export type DonationProductId = `DONATION_${(typeof DONATION_AMOUNTS)[number]}`;
export const DONATION_PRODUCTS: readonly (Product & { id: DonationProductId })[] = DONATION_AMOUNTS.map((usd) => ({
  id: `DONATION_${usd}` as DonationProductId, priceCents: usd * 100, kind: 'one-time' as const,
  gives: { donation: true as const }, store: { productId: `donation_${usd}` },
}));
export const isDonation = (id: string | null | undefined): id is DonationProductId => DONATION_PRODUCTS.some((p) => p.id === id);

/** Every product a store or the web may sell: the catalogue, the Independent Rave price steps, then the donations. */
export const ALL_PRODUCTS: readonly Product[] = [...PRODUCT_IDS.map((id) => PRODUCTS[id]), ...RAVE_PRICE_PRODUCTS, ...DONATION_PRODUCTS];

/** The catalogue product a store product id names, or null. */
export function productForStoreId(storeProductId: string): Product | null {
  return ALL_PRODUCTS.find((p) => p.store.productId === storeProductId) ?? null;
}

/** A product by its id — a catalogue product or a price step — or null. */
export function productById(id: string | null | undefined): Product | null {
  if (!id) return null;
  return ALL_PRODUCTS.find((p) => p.id === id) ?? null;
}

/** Does this product give one rave (the one-rave price, or an Independent Rave's own)? */
export const givesOneRave = (id: string | null | undefined): boolean => {
  const p = productById(id);
  return p !== null && 'rave' in p.gives;
};

export const tierProduct = (tier: Tier): Product => PRODUCTS[`TIER_${tier}` as ProductId];

/**
 * What a photo costs this person, in cents, and which product that is: free
 * on MAX and within PLUS's ten a year, PLUS's 40% off after, $8 otherwise.
 */
export function photoPrice(
  tier: Tier | null, freePhotosLeft: number | null, cat: Catalogue = resolveCatalogue(null),
): { priceCents: number; product: ProductId | null } {
  if (tier === 'MAX') return { priceCents: 0, product: null };
  if (tier === 'PLUS') {
    return (freePhotosLeft ?? 0) > 0
      ? { priceCents: 0, product: null }
      : { priceCents: cat.priceCents.PHOTO_PLUS, product: 'PHOTO_PLUS' };
  }
  return { priceCents: cat.priceCents.PHOTO, product: 'PHOTO' };
}

/**
 * What a platform admin changed about the catalogue (the user, 2026-10-09:
 * Plans "missing edit option") — prices and what a tier gives. Anything not
 * named is the code's own value above. Store prices are the stores' (set in
 * App Store Connect and Google Play); this is what the WEB charges and what
 * every screen says.
 */
export interface CatalogueOverrides {
  prices?: Partial<Record<ProductId, number>>;
  tiers?: Partial<Record<Tier, { ravesPerYear?: number | null; freePhotosPerYear?: number | null }>>;
}

/** The catalogue in force: the code's values, with an admin's changes laid over them. */
export interface Catalogue {
  priceCents: Record<ProductId, number>;
  ravesPerYear: Record<Tier, number | null>;
  freePhotosPerYear: Record<Tier, number | null>;
}

export function resolveCatalogue(o: CatalogueOverrides | null | undefined): Catalogue {
  const priceCents = Object.fromEntries(PRODUCT_IDS.map((id) => [id, o?.prices?.[id] ?? PRODUCTS[id].priceCents])) as Record<ProductId, number>;
  const tiers: Tier[] = ['LITE', 'PLUS', 'MAX'];
  const pick = (t: Tier, k: 'ravesPerYear' | 'freePhotosPerYear', d: number | null) => {
    const v = o?.tiers?.[t]?.[k];
    return v === undefined ? d : v;
  };
  return {
    priceCents,
    ravesPerYear: Object.fromEntries(tiers.map((t) => [t, pick(t, 'ravesPerYear', TIER_RAVES_PER_YEAR[t])])) as Record<Tier, number | null>,
    freePhotosPerYear: Object.fromEntries(tiers.map((t) => [t, pick(t, 'freePhotosPerYear', TIER_FREE_PHOTOS_PER_YEAR[t])])) as Record<Tier, number | null>,
  };
}

/**
 * What an account holds — `GET /me/entitlements`, the Plans screen and the
 * paywall read it. Derived on the server from purchases each time it is asked.
 */
export interface EntitlementSummary {
  tier: null | {
    tier: Tier;
    /** Where it is billed — and so where it is managed. */
    provider: 'stripe' | 'apple' | 'google';
    /** The end of the year it is in; renews then unless `autoRenew` is off. */
    periodEnd: string;
    autoRenew: boolean;
    /** Raves left to open this year; null = unlimited (MAX). */
    ravesLeft: number | null;
    /** Free photos left this year; null = unlimited (MAX); 0 on LITE. */
    photosLeft: number | null;
  };
  /** Pack credits not yet used. They never expire. */
  raveCredits: number;
  /** A photo paid for in a store whose photo could not be given at once. */
  photoCredits: number;
  /** Raves this account owns for good. */
  editionsOwned: number;
  /** Likes without the daily limit — any tier. */
  unlimitedLikes: boolean;
  /**
   * A platform administrator's plan, "Master Raver" (the user, 2026-10-10):
   * unlimited access to everything, nothing to buy, nothing to change.
   * Absent for everyone else; `tier` stays null for a master.
   */
  master?: true;
  plan?: 'MASTER';
}

export function formatUsd(cents: number, locale = 'en'): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
